import { css } from '@emotion/react';
import { ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ApiPropertyProject } from '@kaiizen/kai-pro-api';

import { KaiLogoWithName } from '@/Client/Icons';
import { useKaiProApiClient } from '../State';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../Styles';
import { Button } from '../Widgets';

type ContractorLinkInfo = {
  project?: ApiPropertyProject;
  contractorName: string;
  orgName?: string;
  alreadyOnboarded: boolean;
  contactEmail?: string;
  contactPhoneNumber?: string;
};

const MOBILE_BREAKPOINT = 900;

const PAGE_STYLE = css`
  label: CONTRACTOR_LINK_PAGE;
  ${TYPOGRAPHY2.small}
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background: var(--background);
`;

const LEFT_STYLE = css`
  label: CONTRACTOR_LINK_LEFT;
  flex: 1;
  height: 100vh;
  overflow-y: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FORM_WRAPPER_STYLE = css`
  width: 100%;
  max-width: 400px;
  padding: ${LAYOUT2.spacing4}px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${LAYOUT2.spacing2}px;
`;

const HERO_STYLE = (url?: string) =>
  css`
  label: CONTRACTOR_LINK_HERO;
  width: 50vw;
  height: 100vh;
  background-color: ${COLORS2.namedColors.lightestGray};
  ${url ? `background-image: url(${url});` : ''}
  background-size: cover;
  background-position: center;
`;

const MOBILE_HERO_STYLE = (url?: string) =>
  css`
  width: 100vw;
  height: 240px;
  background-color: ${COLORS2.namedColors.lightestGray};
  ${url ? `background-image: url(${url});` : ''}
  background-size: cover;
  background-position: center;
`;

const EYEBROW_STYLE = css`
  ${TYPOGRAPHY2.xSmallBold}
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--text2);
`;

const HEADLINE_STYLE = css`
  ${TYPOGRAPHY2.xxLargeBold}
  color: var(--text);
  margin: 0;
`;

const ADDRESS_STYLE = css`
  ${TYPOGRAPHY2.large}
  color: var(--text);
`;

const META_STYLE = css`
  ${TYPOGRAPHY2.small}
  color: var(--text2);
`;

const HELPER_STYLE = css`
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
`;

const SUPPORT_STYLE = css`
  ${TYPOGRAPHY2.small}
  color: var(--text);
`;

const ACTIONS_STYLE = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
  margin-top: ${LAYOUT2.spacing1}px;
`;

export const ContractorLinkLandingPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const apiClient = useKaiProApiClient();

  const [info, setInfo] = useState<ContractorLinkInfo>();
  const [error, setError] = useState<string>();
  const [heroUrl, setHeroUrl] = useState<string>();
  const [sentTo, setSentTo] = useState<string>();
  const [sentChannel, setSentChannel] = useState<'email' | 'sms'>();
  const [busy, setBusy] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!token) {
      setError('This link is missing its token.');
      return;
    }
    apiClient.getContractorLinkInfo(token).then(setInfo).catch(() => setError('This link is invalid or has expired.'));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const address = info?.project?.orgProperty?.address;
  useEffect(() => {
    if (!address) return;
    let objectUrl: string | undefined;
    let cancelled = false;
    apiClient.getStreetViewImage({ address, width: 1000, height: 1200 })
      .then(blob => {
        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setHeroUrl(objectUrl);
        }
      })
      .catch(() => {/* hero is decorative */});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [address]); // eslint-disable-line react-hooks/exhaustive-deps

  const startClaim = async (channel: 'email' | 'sms') => {
    setBusy(true);
    try {
      const { contact } = await apiClient.initiateContractorClaim({ token, channel });
      setSentTo(contact);
      setSentChannel(channel);
    } catch {
      setError("We couldn't send your link. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Responsive split: desktop = left panel + full-bleed hero on the right; mobile = hero banner on top.
  const renderShell = (children: ReactNode) => (
    <div css={PAGE_STYLE} style={isMobile ? { flexDirection: 'column', height: 'auto', minHeight: '100vh', overflow: 'visible' } : undefined}>
      {isMobile && <div css={MOBILE_HERO_STYLE(heroUrl)} />}
      <div css={LEFT_STYLE} style={isMobile ? { height: 'auto' } : undefined}>
        <div css={FORM_WRAPPER_STYLE}>
          <KaiLogoWithName />
          {children}
        </div>
      </div>
      {!isMobile && <div css={HERO_STYLE(heroUrl)} />}
    </div>
  );

  if (error) {
    return renderShell(
      <>
        <h1 css={HEADLINE_STYLE}>Link unavailable</h1>
        <div css={META_STYLE}>{error}</div>
      </>,
    );
  }
  if (!info) return renderShell(<div css={META_STYLE}>Loading…</div>);

  // Confirmation state — a claim link was just sent.
  if (sentTo) {
    return renderShell(
      <>
        <div css={EYEBROW_STYLE}>{sentChannel === 'sms' ? 'Check your phone' : 'Check your email'}</div>
        <h1 css={HEADLINE_STYLE}>Link on its way</h1>
        <div css={META_STYLE}>
          We sent a secure link to <strong>{sentTo}</strong>. Open it to continue — it expires shortly.
        </div>
      </>,
    );
  }

  return renderShell(
    <>
      {info.orgName && <div css={EYEBROW_STYLE}>Invited by {info.orgName}</div>}
      <h1 css={HEADLINE_STYLE}>{info.alreadyOnboarded ? 'Welcome back' : 'Project invitation'}</h1>
      {address && <div css={ADDRESS_STYLE}>{address}</div>}
      <div css={META_STYLE}>For {info.contractorName}</div>
      <div css={SUPPORT_STYLE}>
        {info.alreadyOnboarded
          ? 'Log in to view this project and its scope of work.'
          : 'Create your account to view this project and its scope of work.'}
      </div>

      {info.contactEmail || info.contactPhoneNumber
        ? (
          <div css={ACTIONS_STYLE}>
            {info.contactEmail && (
              <>
                <Button size='large' width='100%' disabled={busy} onClick={() => startClaim('email')}>
                  {busy ? 'Sending…' : 'Email me a secure link'}
                </Button>
                <div css={HELPER_STYLE}>We'll send it to {info.contactEmail}</div>
              </>
            )}
            {info.contactPhoneNumber && (
              <>
                <Button size='large' width='100%' buttonStyle={info.contactEmail ? 'white' : undefined} disabled={busy} onClick={() => startClaim('sms')}>
                  {busy ? 'Sending…' : info.contactEmail ? 'Text me a link instead' : 'Text me a secure link'}
                </Button>
                {!info.contactEmail && <div css={HELPER_STYLE}>We'll text it to {info.contactPhoneNumber}</div>}
              </>
            )}
          </div>
        )
        : <div css={META_STYLE}>No contact is on file yet. Please ask your project contact to add your email or phone.</div>}
    </>,
  );
};
